/* eslint-disable no-restricted-syntax */
/**
 * categoryHelpers.js
 * ─────────────────────────────────────────────────────────
 * Helper utilities for CMS and category-related components.
 */

/**
 * Extracts a category from the overall data structure based on urlKey.
 *
 * @param {object} data   - The full GraphQL data
 * @param {string} urlKey - The key to look for (e.g. 'sauces')
 * @returns {object|null}
 */
export const getCategoryByUrlKey = (data, urlKey) => {
  if (!data?.categories?.items) return null;
  return data.categories.items.find((c) => c.urlKey === urlKey) || null;
};

/**
 * Robustly extracts plain text from an EverShop description field.
 * Handles:
 *  - JSON strings
 *  - Array of sections (each with columns/blocks)
 *  - Single section objects
 *  - Plain strings
 *
 * @param {any} description
 * @returns {string}
 */
export const extractDescriptionText = (description) => {
  if (!description) return '';

  try {
    // 1. Handle JSON strings — attempt to parse and recurse
    if (typeof description === 'string') {
      const trimmed = description.trim();
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && typeof parsed === 'object') {
            return extractDescriptionText(parsed);
          }
        } catch (e) {
          return trimmed;
        }
      }
      return trimmed;
    }

    // 2. Handle object with a nested 'description' property (common EverShop wrapper)
    if (
      typeof description === 'object' &&
      !Array.isArray(description) &&
      description.description
    ) {
      return extractDescriptionText(description.description);
    }

    // 3. Handle array of sections or strings
    if (Array.isArray(description)) {
      if (description.length === 0) return '';

      // If the first item is a section object with columns, process all sections
      if (typeof description[0] === 'object' && description[0].columns) {
        return description
          .map((section) => extractDescriptionText(section))
          .filter(Boolean)
          .join('\n')
          .trim();
      }

      // Otherwise treat as array of simple strings
      return description
        .map((item) => (typeof item === 'string' ? item : ''))
        .filter(Boolean)
        .join(' ')
        .trim();
    }

    // 4. Handle a single section object (columns -> blocks)
    if (description.columns && Array.isArray(description.columns)) {
      const allText = [];
      for (const column of description.columns) {
        if (column.data?.blocks && Array.isArray(column.data.blocks)) {
          for (const block of column.data.blocks) {
            if (block.type === 'paragraph' && block.data?.text) {
              allText.push(block.data.text);
            }
          }
        }
      }
      return allText.join(' ').trim();
    }

    // 5. Handle object with a 'text' property
    if (typeof description === 'object' && description.text) {
      return String(description.text).trim();
    }

    return '';
  } catch (e) {
    return '';
  }
};

/**
 * Filters out products that should not be shown in the menu.
 * Removes products with no price or that are not enabled.
 *
 * @param {object} category - Category object with products
 * @returns {Array}
 */
export const processProducts = (category) => {
  if (!category?.products?.items) return [];
  return category.products.items.filter((product) => {
    if (!product) return false;
    return true;
  });
};

/**
 * Creates a DOM-friendly category ID string.
 *
 * @param {string} name      - The category name
 * @param {string} [prefix]  - Optional prefix (default: 'category')
 * @returns {string}
 */
export const createCategoryId = (name, prefix = 'category') => {
  if (!name) return prefix;
  return `${prefix}-${name.toLowerCase().replace(/\s+/g, '-')}`;
};

/**
 * Filters and sorts categories for menu display.
 * Excludes hidden/disabled categories and orders by the CATEGORY_ORDER list.
 *
 * @param {Array} categories - Raw categories array from GraphQL
 * @returns {Array}
 */
export const filterAndSortCategories = (categories) => {
  if (!Array.isArray(categories)) return [];

  const EXCLUDED = /^(2nd Side|1st Side)$/i;
  const CATEGORY_ORDER = [
    'Sandwiches',
    'Family Box',
    'Meals',
    'Kids Meals',
    'Appetizers',
    'Rizo',
    'Fries',
    'Combo',
    'Extras',
    'Drinks'
  ];

  const filtered = categories.filter((cat) => {
    if (!cat) return false;
    if (cat.status === 0 || cat.status === '0') return false;
    if (EXCLUDED.test(cat.name)) return false;
    return true;
  });

  return filtered.sort((a, b) => {
    const aIdx = CATEGORY_ORDER.indexOf(a.name);
    const bIdx = CATEGORY_ORDER.indexOf(b.name);
    if (aIdx === -1 && bIdx === -1) return 0;
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });
};
